import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { businessSlice } from '../../../../../../features/business/businessSlice';
import ProtectedPaymentRoute from '../../../../../auth/Auth';

export default function ContactDetails() {
  ProtectedPaymentRoute();

  const actions = businessSlice.actions;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const businessTypeId = useSelector((state) => state.business.businessTypeId);
  const location = useLocation();
  const savedPrimaryContact = useSelector((state) => state.business.primaryContact);
  const [primaryContact, setPrimaryContact] = useState({
    fullName: savedPrimaryContact?.fullName || '',
    email: savedPrimaryContact?.email || '',
  });

  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
  });

  const validateAlphabets = (text) => /^[a-zA-Z\s]+$/.test(text);
  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleInputChange = (field, value) => {
    let isValid = true;
    let errorMsg = "";

    if (field === "fullName") {
      if (!validateAlphabets(value)) {
        isValid = false;
        errorMsg = "Full name should contain only letters and spaces";
      }
    }

    if (!isValid) {
      setErrors((prevErrors) => ({ ...prevErrors, [field]: errorMsg }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
    }

    setPrimaryContact((prev) => ({ ...prev, [field]: value }));
  };

  const validateInputs = () => {
    let isValid = true;
    const updatedErrors = {};

    if (!primaryContact.fullName.trim()) {
      updatedErrors.fullName = 'Please enter your full name';
      isValid = false;
    } else if (!validateAlphabets(primaryContact.fullName)) {
      updatedErrors.fullName = 'Full name should contain only letters and spaces';
      isValid = false;
    }

    if (!primaryContact.email.trim()) {
      updatedErrors.email = 'Please enter your email address';
      isValid = false;
    } else if (!validateEmail(primaryContact.email)) {
      updatedErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(updatedErrors);
    return isValid;
  };

  const handleNextButtonClick = (event) => {
    event.preventDefault();

    if (validateInputs()) {
      dispatch(actions.update_busniness_primary_contact({
        fullName: primaryContact.fullName,
        email: primaryContact.email,
      }));

      const searchParams = new URLSearchParams(location.search);
      const editParam = searchParams.get("edit");
      if (editParam === "true") {
        navigate("/invoice");
      } else {
        if (businessTypeId === 1) {
          navigate("/business_type");
        } else {
          navigate("/corp_question/1");
        }
      }
    }
  };

  const handleNextPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleNextButtonClick(event);
    }
  };

  const backbtn = () => {
    if (businessTypeId === 1) {
      navigate("/business_location");
    } else {
      navigate("/corpo_help");
    }
  };

  const isEdit = new URLSearchParams(location.search).get("edit") === "true";

  return (
    <div className="min-h-[75vh] bg-white py-12 lg:py-20 font-sans flex items-center">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          
          {/* Left Graphic */}
          <div className="lg:col-span-5 flex justify-center items-center">
            <img 
              src="/Assets/Form_img/communicate.png" 
              alt="Primary Contact" 
              className="w-full max-w-[340px] h-auto object-contain"
            />
          </div>

          {/* Right Form */}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
                Primary Contact Details
              </span>
              <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
                Who will be the primary contact?
              </h1>
              <p className="text-[15px] text-[#5f6368] leading-relaxed">
                Provide contact information so we can share filing updates and confirmation documents. We protect your privacy and never sell your information.
              </p>
            </div>

            <form onKeyDown={handleNextPress} className="space-y-5 max-w-xl">
              {/* Full Name */}
              <div>
                <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                  Full Name <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. John Doe"
                  value={primaryContact.fullName}
                  onChange={(e) => handleInputChange("fullName", e.target.value)}
                  className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                    errors.fullName ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                  } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                />
                {errors.fullName && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                  Email Address <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <input
                  type="email"
                  placeholder="e.g. john@example.com"
                  value={primaryContact.email}
                  onChange={(e) => setPrimaryContact((prev) => ({ ...prev, email: e.target.value }))}
                  className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                    errors.email ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                  } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                />
                {errors.email && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 pt-6">
                {!isEdit && (
                  <button
                    type="button"
                    onClick={backbtn}
                    className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f8f9fa] transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
                  >
                    <i className="fa-solid fa-arrow-left text-xs"></i> Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNextButtonClick}
                  className="px-8 py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all flex items-center gap-2 cursor-pointer text-[14px]"
                >
                  {isEdit ? "Update Contact" : "Next"} <i className="fa-solid fa-arrow-right text-xs"></i>
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}
